import { useState } from 'react';
import { MessageSquare, Loader2 } from 'lucide-react';
import {
  useCommunicationTimeline,
  type TimelineEventKind,
} from '../hooks/useCommunicationTimeline.js';
import { TimelineEventRow } from '../components/TimelineEvent.js';
import { TimelineFilters } from '../components/TimelineFilters.js';

interface Props {
  /** Si fourni, filtre la timeline sur cet apprenant ; sinon vue globale. */
  apprenantId?: string;
  /** Affiche un lien "voir fiche" pour chaque événement (utile en vue globale). */
  showApprenantLinks?: boolean;
  title?: string;
  limit?: number;
}

/**
 * Slice autonome — Timeline communication unifiée.
 * Utilisable en vue globale (page /communications) ET en vue par apprenant.
 */
export function UnifiedTimelineCard({
  apprenantId,
  showApprenantLinks = !apprenantId,
  title = 'Timeline communication',
  limit = 100,
}: Props) {
  const [kinds, setKinds] = useState<TimelineEventKind[]>([]);
  const {
    data: events,
    isLoading,
    error,
  } = useCommunicationTimeline({
    apprenantId,
    kinds: kinds.length ? kinds : undefined,
    limit,
  });

  return (
    <section className="rounded-xl border border-neutral-200 bg-white">
      <header className="flex flex-wrap items-center gap-3 border-b border-neutral-200 px-4 py-3">
        <MessageSquare className="text-primary-600 h-4 w-4" />
        <h3 className="text-sm font-bold text-neutral-900">{title}</h3>
        {events && (
          <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-semibold text-neutral-600">
            {events.length}
          </span>
        )}
        <div className="ml-auto">
          <TimelineFilters selected={kinds} onChange={setKinds} />
        </div>
      </header>

      <div className="space-y-2 p-3">
        {isLoading && (
          <div className="flex items-center gap-2 py-6 text-xs text-neutral-500">
            <Loader2 className="h-3 w-3 animate-spin" />
            Chargement de la timeline…
          </div>
        )}
        {error && (
          <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
            Erreur chargement : {(error as Error).message}
          </div>
        )}
        {!isLoading && events && events.length === 0 && (
          <div className="py-6 text-center text-xs italic text-neutral-500">
            Aucune communication pour l'instant.
            {!apprenantId && (
              <div className="mt-1 text-[11px]">
                Les emails sortants seront loggés ici dès l'instrumentation des senders (vague 2).
              </div>
            )}
          </div>
        )}
        {events?.map((ev, i) => (
          <TimelineEventRow
            key={`${ev.kind}-${ev.email?.id ?? ev.note?.id ?? i}`}
            event={ev}
            showApprenantLink={showApprenantLinks}
          />
        ))}
      </div>
    </section>
  );
}
